import React from 'react';
import {
  Box, TextField, FormGroup, FormControlLabel, Checkbox, RadioGroup, Radio,
} from '@mui/material';
import {
  GRADE_OPTIONS, U_BASED_OPTIONS, AGE_OPTIONS, NAMED_DIVISION_OPTIONS,
  NAMED_DIVISION_OTHER, FORMAT_OPTIONS, FOOTBALL_TYPE_OPTIONS, FOOTBALL_TYPE_LABELS,
  sortClassificationValues,
} from '../../utils/teamClassification';

// Shared Team classification/format input controls (028) — used by both
// CreateSeasonTeamPage.js and TeamSettingsPage.js so the two editing
// surfaces can never silently drift apart.

const MULTI_SELECT_OPTIONS = {
  grade: GRADE_OPTIONS,
  u_based: U_BASED_OPTIONS,
  age: AGE_OPTIONS,
};

/** Grade/U-Based/Age share the same multi-select-checkbox shape, just a
 * different fixed option list (utils/teamClassification.js) — one
 * reusable control for all three rather than three near-duplicates. */
export function MultiSelectClassification({ type, values, onChange }) {
  const options = MULTI_SELECT_OPTIONS[type];
  const toggle = (opt) => {
    const next = values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt];
    onChange(sortClassificationValues(type, next));
  };
  return (
    <FormGroup row sx={{ gap: 0.5 }}>
      {options.map(opt => (
        <FormControlLabel
          key={opt}
          control={<Checkbox size="small" checked={values.includes(opt)} onChange={() => toggle(opt)} />}
          label={opt}
          sx={{ mr: 1.5 }}
        />
      ))}
    </FormGroup>
  );
}

/** Named Division is single-value-only, with "Other" revealing a free-
 * text field whose typed value IS the (single) classification_values
 * entry — no sentinel is ever sent to the backend. */
export function NamedDivisionClassification({ choice, otherText, onChoiceChange, onOtherTextChange }) {
  return (
    <Box>
      <RadioGroup value={choice} onChange={e => onChoiceChange(e.target.value)}>
        {NAMED_DIVISION_OPTIONS.map(opt => (
          <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={opt} />
        ))}
        <FormControlLabel value={NAMED_DIVISION_OTHER} control={<Radio size="small" />} label="Other" />
      </RadioGroup>
      {choice === NAMED_DIVISION_OTHER && (
        <TextField
          fullWidth size="small" label="Custom division name" value={otherText}
          onChange={e => onOtherTextChange(e.target.value)}
          placeholder="e.g. Freshman"
          sx={{ mt: 1, maxWidth: 320 }}
        />
      )}
    </Box>
  );
}

/** Football type (029) — Flag or Tackle, exactly one required. No
 * "Other" in this phase; visually and conceptually independent from
 * Classification and Football Format (rendered as its own section by
 * callers), even though it's a plain single-select radio like Named
 * Division's non-"Other" options. */
export function FootballTypePicker({ value, onChange }) {
  return (
    <RadioGroup row value={value || ''} onChange={e => onChange(e.target.value)}>
      {FOOTBALL_TYPE_OPTIONS.map(opt => (
        <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={FOOTBALL_TYPE_LABELS[opt]} />
      ))}
    </RadioGroup>
  );
}

/** Football format — same "fixed options + Other reveals a raw input"
 * shape as Named Division, but the underlying value is always the
 * integer players_per_side, never a string like "8v8". */
export function FormatPicker({ choice, otherText, onChoiceChange, onOtherTextChange }) {
  return (
    <Box>
      <RadioGroup row value={choice} onChange={e => onChoiceChange(e.target.value)}>
        {FORMAT_OPTIONS.map(opt => (
          <FormControlLabel key={opt.value} value={String(opt.value)} control={<Radio size="small" />} label={opt.label} />
        ))}
        <FormControlLabel value="other" control={<Radio size="small" />} label="Other" />
      </RadioGroup>
      {choice === 'other' && (
        <TextField
          size="small" type="number" label="Players per side" value={otherText}
          onChange={e => onOtherTextChange(e.target.value)}
          inputProps={{ min: 4, max: 15, step: 1 }}
          sx={{ mt: 1, maxWidth: 200 }}
        />
      )}
    </Box>
  );
}
